//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Typography } from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2' // Grid version 2
import { EthAddr } from '@shared/SchemaTypes'
import {
  PoolBaseOverviewCTOSchema,
  PoolFlexOverviewCTO,
  PoolFlexOverviewCTOSchema,
  PoolOverviewCTO,
  PoolOverviewCTOSchema,
} from '@shared/sharedTypes'
import React from 'react'
import { useParams } from 'react-router-dom'

import ViewField from '@react/components/elements/ViewField'
import { OtStoreState, useOtSelector } from '@react/redux/store'

import { SectionHeader } from '@react/components/common/SectionHeader'
import { PoolTypeEnum } from '@shared/Enums'
import { PoolDynamicOverviewCTO, PoolDynamicOverviewCTOSchema } from '@shared/PoolDynamicCTO'

import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import PoolDetailsSkeleton from '@react/components/skeletons/PoolDetailsSkeleton'
import { logOT } from '@shared/loggingShared'

export default function PoolOverview(props: { poolAddr?: EthAddr }): React.ReactElement {
  const p = useParams().poolAddr
  const poolAddr = (props.poolAddr ? props.poolAddr : p) as EthAddr

  const poolBaseOverviewCTO = useOtSelector(
    (state: OtStoreState) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO]
  )

  const navigate = useNavigate()

  useEffect(() => {
    if (!poolBaseOverviewCTO) {
      const timeout = setTimeout(() => {
        if (!poolBaseOverviewCTO) {
          logOT.info('location navigate to /')
          navigate('/')
        }
      }, 1000)

      return () => clearTimeout(timeout)
    }
  }, [poolBaseOverviewCTO, navigate])

  if (!poolBaseOverviewCTO) {
    return <PoolDetailsSkeleton />
  }
  const poolOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Term ? (poolBaseOverviewCTO as PoolOverviewCTO) : null
  const poolFlexOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate ? (poolBaseOverviewCTO as PoolFlexOverviewCTO) : null
  const poolDynamicOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Dynamic ? (poolBaseOverviewCTO as PoolDynamicOverviewCTO) : null

  return (
    <Box px={3} py={2} sx={{ flexGrow: 1, margin: 'auto' }}>
      <SectionHeader>
        <Typography variant={'captionBold'}>Summary</Typography>
      </SectionHeader>

      <Grid container spacing={8} sx={{ padding: '12px' }}>
        {poolDynamicOverviewCTO && (
          <>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolDynamicOverviewCTO}
                fieldSchema={PoolDynamicOverviewCTOSchema.describe().fields.totalOutstandingLoanPrincipal}
                liquidityTokenSymbol={poolDynamicOverviewCTO.liquidityTokenSymbol}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolDynamicOverviewCTO}
                fieldSchema={PoolDynamicOverviewCTOSchema.describe().fields.totalShares}
                liquidityTokenSymbol={poolDynamicOverviewCTO.liquidityTokenSymbol}
              ></ViewField>
            </Grid>

            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolDynamicOverviewCTO}
                fieldSchema={PoolDynamicOverviewCTOSchema.describe().fields.interestRate}
              ></ViewField>
            </Grid>

            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.benchmarkRate}
              ></ViewField>
            </Grid>

            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolDynamicOverviewCTO}
                poolTokenSymbol={poolDynamicOverviewCTO.symbol}
                fieldSchema={PoolDynamicOverviewCTOSchema.describe().fields.exchangeRate}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolDynamicOverviewCTO}
                poolTokenSymbol={poolDynamicOverviewCTO.symbol}
                fieldSchema={PoolDynamicOverviewCTOSchema.describe().fields.exchangeRateType}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolDynamicOverviewCTO}
                fieldSchema={PoolDynamicOverviewCTOSchema.describe().fields.indicativeInterestRate}
              ></ViewField>
            </Grid>

            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolDynamicOverviewCTO}
                fieldSchema={PoolDynamicOverviewCTOSchema.describe().fields.collateralRate}
              ></ViewField>
            </Grid>
            <Grid xs={12} key={'VaultConfig'}>
              <SectionHeader>Vault Configuration</SectionHeader>
            </Grid>

            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolDynamicOverviewCTO}
                fieldSchema={PoolDynamicOverviewCTOSchema.describe().fields.closeOfDepositTime}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolDynamicOverviewCTO}
                fieldSchema={PoolDynamicOverviewCTOSchema.describe().fields.closeOfWithdrawTime}
              ></ViewField>
            </Grid>

            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.transferInDays}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolDynamicOverviewCTO}
                fieldSchema={PoolDynamicOverviewCTOSchema.describe().fields.transferOutDays}
              ></ViewField>
            </Grid>

            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.advisoryFeeRate}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.platformFeeRate}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.liquidityFeeRate}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.contractualCurrency}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.minimumTransferAmount}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.maximumTransferAmount}
              ></ViewField>
            </Grid>

            <Grid xs={12} key={'HistPerformance'}>
              <SectionHeader>Historical Performance</SectionHeader>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolDynamicOverviewCTO}
                fieldSchema={PoolDynamicOverviewCTOSchema.describe().fields.totalAssetsDeposited}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolDynamicOverviewCTO}
                fieldSchema={PoolDynamicOverviewCTOSchema.describe().fields.totalAssetsWithdrawn}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              {/* <ViewField
                dataObject={poolDynamicOverviewCTO}
                fieldSchema={PoolDynamicOverviewCTOSchema.describe().fields.totalInterestAccrued}
              ></ViewField> */}
            </Grid>
          </>
        )}
        {poolFlexOverviewCTO && (
          <>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolFlexOverviewCTO}
                fieldSchema={PoolFlexOverviewCTOSchema.describe().fields.totalPrincipalEarningInterest}
                liquidityTokenSymbol={poolFlexOverviewCTO.liquidityTokenSymbol}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolFlexOverviewCTO}
                poolTokenSymbol={poolFlexOverviewCTO.symbol}
                fieldSchema={PoolFlexOverviewCTOSchema.describe().fields.totalSupply}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolFlexOverviewCTO}
                fieldSchema={PoolFlexOverviewCTOSchema.describe().fields.interestRate}
              ></ViewField>
            </Grid>

            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.benchmarkRate}
              ></ViewField>
            </Grid>

            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolFlexOverviewCTO}
                poolTokenSymbol={poolFlexOverviewCTO.symbol}
                fieldSchema={PoolFlexOverviewCTOSchema.describe().fields.exchangeRate}
              ></ViewField>
            </Grid>
            <Grid xs={12} key={'VaultConfig'}>
              <SectionHeader>Vault Configuration</SectionHeader>
            </Grid>

            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolFlexOverviewCTO}
                fieldSchema={PoolFlexOverviewCTOSchema.describe().fields.closeOfDepositTime}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolFlexOverviewCTO}
                fieldSchema={PoolFlexOverviewCTOSchema.describe().fields.closeOfWithdrawTime}
              ></ViewField>
            </Grid>

            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolFlexOverviewCTO}
                fieldSchema={PoolFlexOverviewCTOSchema.describe().fields.transferInDays}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolFlexOverviewCTO}
                fieldSchema={PoolFlexOverviewCTOSchema.describe().fields.transferOutDays}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.benchmarkRate}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.advisoryFeeRate}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.platformFeeRate}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.liquidityFeeRate}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.contractualCurrency}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.minimumTransferAmount}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.maximumTransferAmount}
              ></ViewField>
            </Grid>

            <Grid xs={12} key={'HistPerformance'}>
              <SectionHeader>Historical Performance</SectionHeader>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolFlexOverviewCTO}
                fieldSchema={PoolFlexOverviewCTOSchema.describe().fields.totalAssetsDeposited}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolFlexOverviewCTO}
                fieldSchema={PoolFlexOverviewCTOSchema.describe().fields.totalAssetsWithdrawn}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolFlexOverviewCTO}
                fieldSchema={PoolFlexOverviewCTOSchema.describe().fields.totalInterestAccrued}
              ></ViewField>
            </Grid>
          </>
        )}
        {poolOverviewCTO && (
          <>
            <Grid xs={12} sm={6} sx={{ flexGrow: 1 }}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolOverviewCTOSchema.describe().fields.maxCapacity}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolBaseOverviewCTO}
                fieldSchema={PoolBaseOverviewCTOSchema.describe().fields.totalOutstandingLoanPrincipal}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolOverviewCTO}
                fieldSchema={PoolOverviewCTOSchema.describe().fields.currentExpectedInterest}
              ></ViewField>
            </Grid>
            {/* Requests for Withdrawal at Maturity = USDC to be repaid when the loan matures */}
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolOverviewCTO}
                fieldSchema={PoolOverviewCTOSchema.describe().fields.totalAvailableSupply}
                poolTokenSymbol={poolOverviewCTO.symbol}
              ></ViewField>
            </Grid>
            <Grid xs={12} key={'history'}>
              <SectionHeader>Historical performance</SectionHeader>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolOverviewCTO}
                fieldSchema={PoolOverviewCTOSchema.describe().fields.totalAssetsDeposited}
              ></ViewField>
            </Grid>
            <Grid xs={12} sm={6}>
              <ViewField
                dataObject={poolOverviewCTO}
                fieldSchema={PoolOverviewCTOSchema.describe().fields.totalAssetsWithdrawn}
              ></ViewField>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  )
}
